import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { subscriptionsService, Subscription } from '@/services/subscriptions';
import { AlertCircle, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { formatDate, formatPrice } from '@/utils/format';

/**
 * Banner que muestra el estado de la suscripcion en el dashboard.
 * - Rojo si esta vencida
 * - Amarillo si vence en los proximos 7 dias
 * - Azul si el pago fue reportado pero no confirmado
 * - Oculto si esta al dia y lejos del vencimiento
 */
export default function BillingBanner() {
  const { data: subscription } = useQuery<Subscription>({
    queryKey: ['subscription', 'me'],
    queryFn: subscriptionsService.getMine,
    retry: false,
  });

  if (!subscription) return null;

  const now = new Date();
  const nextPayment = subscription.next_payment_date ? new Date(subscription.next_payment_date) : null;
  const daysUntilDue = nextPayment ? Math.floor((nextPayment.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;

  const isReported = !!subscription.payment_reported_at;
  const isOverdue = subscription.payment_status === 'overdue' || (daysUntilDue !== null && daysUntilDue < 0);
  const isDueSoon = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 7 && subscription.payment_status !== 'paid';

  if (!isReported && !isOverdue && !isDueSoon) return null;

  if (isReported) {
    return (
      <Link
        to="/dashboard/billing"
        className="block bg-blue-50 border border-blue-200 rounded-xl p-4 hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-blue-900">Pago reportado</p>
            <p className="text-sm text-blue-700">
              Avisaste que pagaste el {formatDate(subscription.payment_reported_at!)}. Estamos verificando la recepción.
            </p>
          </div>
          <ArrowRight className="h-5 w-5 text-blue-600 flex-shrink-0" />
        </div>
      </Link>
    );
  }

  if (isOverdue) {
    return (
      <Link
        to="/dashboard/billing"
        className="block bg-red-50 border border-red-200 rounded-xl p-4 hover:bg-red-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-red-900">Suscripción vencida</p>
            <p className="text-sm text-red-700">
              Tu suscripción de {formatPrice(subscription.price_monthly)} está vencida. Regularizá el pago para seguir usando ANIMAstore.
            </p>
          </div>
          <ArrowRight className="h-5 w-5 text-red-600 flex-shrink-0" />
        </div>
      </Link>
    );
  }

  // isDueSoon
  return (
    <Link
      to="/dashboard/billing"
      className="block bg-amber-50 border border-amber-200 rounded-xl p-4 hover:bg-amber-100 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-amber-900">Próximo vencimiento</p>
          <p className="text-sm text-amber-700">
            Tu suscripción de {formatPrice(subscription.price_monthly)} vence {daysUntilDue === 0 ? 'hoy' : `en ${daysUntilDue} día${daysUntilDue === 1 ? '' : 's'}`} ({formatDate(nextPayment!)}).
          </p>
        </div>
        <ArrowRight className="h-5 w-5 text-amber-600 flex-shrink-0" />
      </div>
    </Link>
  );
}
